import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Button, Table, Row, Col, Alert, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup } from 'reactstrap';
import { withTranslation } from 'react-multi-lang';
import { useToasts } from 'react-toast-notifications';
import IncomingDeliveriesService from '../Services/IncomingDeliveriesService';
import LocalService from '../Services/LocalService';
import AuthService from '../Services/AuthService';
import {VehiclesService} from "../Services/VehiclesService";
import {setSelectionRange} from "@testing-library/user-event/dist/utils";

const IncomingDeliveries = ({ t }) => {
    const [view, setView] = useState(null); // 'cement', 'secondary', or 'compound'
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [compoundPlates, setCompoundPlates] = useState([]);
    const [companyVehicles, setCompanyVehicles] = useState([]);
    const [cementDeliveries, setCementDeliveries] = useState([]);
    const [secondaryDeliveries, setSecondaryDeliveries] = useState([]);
    const [pendingCementDeliveries, setPendingCementDeliveries] = useState([]);
    const [selectedDelivery, setSelectedDelivery] = useState(null);
    const [weighingModal, setWeighingModal] = useState(false);
    const [currentScaleWeight, setCurrentScaleWeight] = useState(0);
    const [weighingInterval, setWeighingInterval] = useState(null);
    const [tare, setTare] = useState(0);
    const [vehicle, setVehicle] = useState(null);

    const service = new IncomingDeliveriesService();
    const localService = new LocalService();
    const authService = new AuthService();
    const { addToast } = useToasts();
    const vehiclesService = new VehiclesService();

    useEffect(() => {
        if (weighingModal) {
            startWeighing();
        } else {
            stopWeighing();
        }
        return () => stopWeighing();
    }, [weighingModal]);

    const startWeighing = () => {
        localService.getWeightFromScale().then(w => {
            setCurrentScaleWeight(w);
            const interval = setInterval(() => {
                localService.getWeightFromScale().then(w => setCurrentScaleWeight(w)).catch(() => {});
            }, 3000);
            setWeighingInterval(interval);
        }).catch(err => {
            console.error(err);
            addToast(t('volumes.localServiceUnreachable'), { appearance: 'error', autoDismiss: true });
        });
    };

    const stopWeighing = () => {
        if (weighingInterval) {
            clearInterval(weighingInterval);
            setWeighingInterval(null);
        }
    };

    const handleRowClick = (item, type) => {
        if (type === 'secondary' || type === 'cement') {
            setSelectedDelivery({ ...item, type });
            setWeighingModal(true);
        }
    };

    const handleInitialCementWeighing = (plate) => {
        const matchingCement = cementDeliveries.find(d => d.Code2 === plate);
        setSelectedDelivery({ 
            plateNumber: plate, 
            type: 'cement', 
            isInitial: true,
            ...(matchingCement ? {
                documentCode: matchingCement.ADCode,
                ItemCode: matchingCement.Code1, 
                Description: matchingCement.Description,
                Name: matchingCement.Name,
                Quantity: matchingCement.Quantity * 1000
            } : {})
        });
        setWeighingModal(true);
    };

    const handleFinalCementWeighing = (delivery) => {
        const matchingCement = cementDeliveries.find(d => d.Code2 === delivery.plateNumber);
        setSelectedDelivery({ 
            ...delivery, 
            isFinal: true,
            Quantity: matchingCement ? matchingCement.Quantity * 1000 : 0, // for comparison
            Description: delivery.itemDescription,
            Name: delivery.supplierName
        });
        setWeighingModal(true);
    };

    const saveWeighing = () => {
        const user = authService.getUser();

        if (selectedDelivery.type === 'cement') {
            if (selectedDelivery.isInitial) {
                const deliveryData = {
                    plateNumber: selectedDelivery.plateNumber,
                    dateTime: new Date(),
                    itemCode: selectedDelivery.ItemCode || '',
                    itemDescription: selectedDelivery.Description || '',
                    quantity: parseFloat(selectedDelivery.Quantity) || 0,
                    vat: 0,
                    supplierName: selectedDelivery.Name || '',
                    price: 0,
                    weightBefore: currentScaleWeight,
                    dateTimeWeightBefore: new Date(),
                    userIdWeightBefore: user?.userId,
                    type: 'cement',
                    documentCode: selectedDelivery.documentCode
                };
                service.createIncomingDelivery(deliveryData).then(() => {
                    addToast(t('incomingDeliveries.weighingSaved'), { appearance: 'success', autoDismiss: true });
                    setWeighingModal(false);
                    if (view === 'compound') fetchCompoundData();
                }).catch(err => {
                    console.error(err);
                    addToast('Error saving weighing', { appearance: 'error', autoDismiss: true });
                });
            } else if (selectedDelivery.isFinal) {
                const deliveryData = {
                    weightAfter: currentScaleWeight,
                    dateTimeWeightAfter: new Date(),
                    userIdWeightAfter: user?.userId
                };
                service.updateIncomingDelivery(selectedDelivery.id, deliveryData).then(() => {
                    addToast(t('incomingDeliveries.weighingSaved'), { appearance: 'success', autoDismiss: true });
                    setWeighingModal(false);
                    if (view === 'compound') fetchCompoundData();
                }).catch(err => {
                    console.error(err);
                    addToast('Error saving weighing', { appearance: 'error', autoDismiss: true });
                });
            }
            return;
        }

        const erpWeight = parseFloat(selectedDelivery.Quantity) * 1000;

        const deliveryData = {
            plateNumber: selectedDelivery.ADStringField4 || '',
            dateTime: new Date(),
            itemCode: selectedDelivery.ItemCode || '',
            itemDescription: selectedDelivery.Description || '',
            quantity: erpWeight,
            vat: 0,
            supplierName: selectedDelivery.Name || '',
            price: parseFloat(selectedDelivery.Price) || 0,
            weightBefore: currentScaleWeight,
            dateTimeWeightBefore: new Date(),
            userIdWeightBefore: user?.userId,
            type: 'secondary',
            documentCode: selectedDelivery.ADCode
        };

        service.createIncomingDelivery(deliveryData).then(() => {
            addToast(t('incomingDeliveries.weighingSaved'), { appearance: 'success', autoDismiss: true });
            setWeighingModal(false);
            if (view === 'compound') fetchCompoundData();
        }).catch(err => {
            console.error(err);
            addToast('Error saving weighing', { appearance: 'error', autoDismiss: true });
        });
    };

    useEffect(() => {
        if (view === 'compound') {
            fetchCompoundData();
        }
    }, [view]);
    
    useEffect(() => {
        if(selectedDelivery){
            vehiclesService.getVehicles(selectedDelivery.ADStringField4.split(' ')[1], 1, 10).then(vehicles => {
                let veh = vehicles[0].find(v => v.plates.includes(selectedDelivery.ADStringField4.split(' ')[1]) && v.weight > 0);
                console.log(veh);
                if(!veh) veh = vehicles[0].find(v => v.plates.includes(selectedDelivery.ADStringField4.split(' ')[1]));
                console.log(veh);
                if(veh){
                    setVehicle(veh);
                    setTare(veh.weight);
                }
            });
        }        
        else setTare(0);
    }, [selectedDelivery]);

    const fetchCompoundData = async () => {
        setLoading(true);
        try {
            const plates = await localService.getVehicles();
            setCompoundPlates(plates);

            const [vehiclesResponse, cement, secondary, pendingCement] = await Promise.all([
                service.getCompanyVehicles(),
                service.getCementPurchases(),
                service.getProductTransfers(),
                service.getIncomingDeliveries(1, 100, '', 'cement', true)
            ]);

            const vehicles = vehiclesResponse[0]; // VehiclesService returns [vehicles, total]
            setCompanyVehicles(vehicles);
            setCementDeliveries(cement);
            setSecondaryDeliveries(secondary);
            setPendingCementDeliveries(pendingCement.items);

            setLoading(false);
        } catch (err) {
            console.error(err);
            setLoading(false);
        }
    };

    const fetchCement = () => {
        setLoading(true);
        setView('cement');
        service.getCementPurchases().then(res => {
            setData(res);
            setLoading(false);
        }).catch(err => {
            console.error(err);
            setLoading(false);
        });
    };

    const fetchSecondary = () => {
        setLoading(true);
        setView('secondary');
        service.getProductTransfers().then(res => {
            setData(res);
            setLoading(false);
        }).catch(err => {
            console.error(err);
            setLoading(false);
        });
    };

    return (
        <Card>
            <CardHeader className="bg-primary text-white">
                <h4>{t('sideBar.incomingDeliveries')}</h4>
            </CardHeader>
            <CardBody>
                <Row className="mb-4">
                    <Col>
                        <Button 
                            color={view === 'cement' ? 'primary' : 'secondary'} 
                            onClick={fetchCement} 
                            className="mr-2"
                            style={{ marginRight: '10px' }}
                        >
                            {t('incomingDeliveries.cementDeliveries')}
                        </Button>
                        <Button 
                            color={view === 'secondary' ? 'primary' : 'secondary'} 
                            onClick={fetchSecondary}
                            style={{ marginRight: '10px' }}
                        >
                            {t('incomingDeliveries.secondaryMaterialsDeliveries')}
                        </Button>
                        <Button 
                            color={view === 'compound' ? 'primary' : 'secondary'} 
                            onClick={() => setView('compound')}
                        >
                            {t('incomingDeliveries.compoundDeliveries')}
                        </Button>
                    </Col>
                </Row>

                {loading && <Alert color="info">Loading...</Alert>}

                {!loading && view === 'compound' && (
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5>{t('vehicles.vehiclesInQuarry')}</h5>
                            <Button size="sm" color="info" onClick={fetchCompoundData}>{t('refresh')}</Button>
                        </div>
                        {compoundPlates.map(plate => {
                            const isCompany = companyVehicles.some(v => v.plates === plate || v.secondPlate === plate);
                            const matchingCement = !isCompany ? cementDeliveries.filter(d => d.Code2 === plate) : [];
                            const matchingSecondary = isCompany ? secondaryDeliveries.filter(d => d.ADStringField4 === plate) : [];
                            const pendingCement = pendingCementDeliveries.find(d => d.plateNumber === plate);

                            return (
                                <Card key={plate} className="mb-3 border-info">
                                    <CardHeader className="bg-info text-white py-1">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span><strong>{plate}</strong> {isCompany ? `(${t('sideBar.vehiclesManagement')})` : ''}</span>
                                            {!isCompany && (
                                                <div>
                                                    {!pendingCement ? (
                                                        <Button size="sm" color="success" onClick={() => handleInitialCementWeighing(plate)}>
                                                            {t('incomingDeliveries.initialWeighing')}
                                                        </Button>
                                                    ) : (
                                                        <Button size="sm" color="warning" onClick={() => handleFinalCementWeighing(pendingCement)}>
                                                            {t('incomingDeliveries.finalWeighing')}
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </CardHeader>
                                    <CardBody className="p-2">
                                        {isCompany ? (
                                            matchingSecondary.length > 0 ? (
                                                <Table responsive size="sm" className="mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th>{t('incomingDeliveries.document')}</th>
                                                            <th>{t('incomingDeliveries.supplier')}</th>
                                                            <th>{t('incomingDeliveries.item')}</th>
                                                            <th>{t('incomingDeliveries.quantity')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {matchingSecondary.map((item, idx) => (
                                                            <tr key={idx} onClick={() => handleRowClick(item, 'secondary')} style={{ cursor: 'pointer' }}>
                                                                <td>{item.ADCode}</td>
                                                                <td>{item.Name}</td>
                                                                <td>{item.Description}</td>
                                                                <td>{(parseFloat(item.Quantity) * 1000).toFixed(2)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            ) : <div className="text-muted small">No matching product transfers</div>
                                        ) : (
                                            // Non-company vehicle -> show cement deliveries
                                            matchingCement.length > 0 ? (
                                                <Table responsive size="sm" className="mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th>{t('incomingDeliveries.document')}</th>
                                                            <th>{t('incomingDeliveries.supplier')}</th>
                                                            <th>{t('incomingDeliveries.item')}</th>
                                                            <th>{t('incomingDeliveries.quantity')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {matchingCement.map((item, idx) => (
                                                            <tr key={idx} onClick={() => handleRowClick(item, 'cement')} style={{ cursor: 'pointer' }}>
                                                                <td>{item.ADCode}</td>
                                                                <td>{item.Name}</td>
                                                                <td>{item.Description}</td>
                                                                <td>{(parseFloat(item.Quantity) * 1000).toFixed(2)}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            ) : <div className="text-muted small">No matching cement deliveries</div>
                                        )}
                                    </CardBody>
                                </Card>
                            );
                        })}
                        {compoundPlates.length === 0 && <Alert color="warning">{t('vehicles.noVehiclesFound')}</Alert>}
                    </div>
                )}

                {!loading && view === 'cement' && (
                    <Table striped responsive>
                        <thead>
                            <tr>
                                <th>{t('incomingDeliveries.date')}</th>
                                <th>{t('incomingDeliveries.document')}</th>
                                <th>{t('incomingDeliveries.supplier')}</th>
                                <th>{t('incomingDeliveries.item')}</th>
                                <th>{t('incomingDeliveries.quantity')}</th>
                                <th>{t('incomingDeliveries.value')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item, index) => (
                                <tr key={index}>
                                    <td>{item.ADRegistrationDate?.split('T')[0]}</td>
                                    <td>{item.ADCode}</td>
                                    <td>{item.Name}</td>
                                    <td>{item.Description}</td>
                                    <td>{(parseFloat(item.Quantity) * 1000).toFixed(2)}</td>
                                    <td>{parseFloat(item.CurrencyNetValue).toFixed(2)}</td>
                                </tr>
                            ))}
                            {data.length === 0 && <tr><td colSpan="6" className="text-center">No data found for today</td></tr>}
                        </tbody>
                    </Table>
                )}

                {!loading && view === 'secondary' && (
                    <Table striped responsive>
                        <thead>
                            <tr>
                                <th>{t('incomingDeliveries.date')}</th>
                                <th>{t('incomingDeliveries.document')}</th>
                                <th>{t('incomingDeliveries.plate')}</th>
                                <th>{t('incomingDeliveries.supplier')}</th>
                                <th>{t('incomingDeliveries.item')}</th>
                                <th>{t('incomingDeliveries.quantity')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item, index) => (
                                <tr key={index} onClick={() => handleRowClick(item, 'secondary')} style={{ cursor: 'pointer' }}>
                                    <td>{item.ADRegistrationDate?.split('T')[0]}</td>
                                    <td>{item.ADCode}</td>
                                    <td>{item.ADStringField4}</td>
                                    <td>{item.Name}</td>
                                    <td>{item.Description}</td>
                                    <td>{(parseFloat(item.Quantity) * 1000).toFixed(2)}</td>
                                </tr>
                            ))}
                            {data.length === 0 && <tr><td colSpan="6" className="text-center">No data found for today</td></tr>}
                        </tbody>
                    </Table>
                )}
            </CardBody>

            <Modal isOpen={weighingModal} toggle={() => setWeighingModal(false)} centered size="lg">
                <ModalHeader toggle={() => setWeighingModal(false)}>
                    {t('incomingDeliveries.weighing')} - {selectedDelivery && (selectedDelivery.ADCode || selectedDelivery.plateNumber || selectedDelivery.documentCode)}
                    {selectedDelivery?.type === 'cement' && (
                        <span className="ml-2 badge badge-info">
                            {selectedDelivery.isInitial ? t('incomingDeliveries.initialWeighing') : t('incomingDeliveries.finalWeighing')}
                        </span>
                    )}
                </ModalHeader>
                <ModalBody>
                    {selectedDelivery && (
                        <div>
                            <Row className="text-center mb-4">
                                <Col md="3">
                                    <h6>{t('incomingDeliveries.erpWeight')}</h6>
                                    <h3 className="text-info">{(parseFloat(selectedDelivery.Quantity || selectedDelivery.quantity || 0) * 1000).toFixed(2)} kg</h3>
                                </Col>
                                <Col md="3">
                                    <h6>Απόβαρο Οχήματος</h6>
                                    <h3>{tare ?? 0} kg</h3>
                                </Col>
                                <Col md="3">
                                    <h6>{t('incomingDeliveries.scaleWeight')}</h6>
                                    <h3 className="text-primary">{currentScaleWeight.toFixed(2)} kg</h3>
                                </Col>
                                <Col md="3">
                                    <h6>{t('incomingDeliveries.difference')}</h6>
                                    {(() => {
                                        const erpWeight = parseFloat(selectedDelivery.Quantity || selectedDelivery.quantity || 0) * 1000;
                                        let diff = 0;
                                        let actualDelivered = 0;
                                        let comparisonPercent = 0;

                                        if (selectedDelivery.type === 'cement' && selectedDelivery.isFinal) {
                                            actualDelivered = Math.abs(parseFloat(selectedDelivery.weightBefore) - currentScaleWeight);
                                            diff = actualDelivered - erpWeight;
                                        } else {
                                            // As per instructions: scale weight minus erp weight (kg) plus tare
                                            diff = currentScaleWeight - (erpWeight + (tare || 0));
                                            actualDelivered = currentScaleWeight + (tare || 0) - erpWeight; // This is not really 'actualDelivered' but following the formula logic
                                        }

                                        comparisonPercent = erpWeight > 0 ? (Math.abs(diff) / (erpWeight + tare)) * 100 : 0;
                                        const isWithinTolerance = comparisonPercent <= 2;

                                        return (
                                            <h3 className={isWithinTolerance ? "text-success" : "text-danger"}>
                                                {diff.toFixed(2)} kg ({comparisonPercent.toFixed(2)}%)
                                            </h3>
                                        );
                                    })()}
                                </Col>
                            </Row>

                            {(() => {
                                const erpWeight = parseFloat(selectedDelivery.Quantity || selectedDelivery.quantity || 0) * 1000;
                                let diff = 0;

                                if (selectedDelivery.type === 'cement' && selectedDelivery.isFinal) {
                                    const actualDelivered = Math.abs(parseFloat(selectedDelivery.weightBefore) - currentScaleWeight);
                                    diff = actualDelivered - erpWeight;
                                } else {
                                    diff = currentScaleWeight - erpWeight + (tare || 0);
                                }
                                
                                const comparisonPercent = erpWeight > 0 ? (Math.abs(diff) / erpWeight) * 100 : 0;
                                const isWithinTolerance = comparisonPercent <= 2;

                                if (selectedDelivery.isInitial) {
                                    return <Alert color="info">Initial weighing for plate: {selectedDelivery.plateNumber}</Alert>;
                                }

                                return (
                                    <Alert color={isWithinTolerance ? 'success' : 'danger'}>
                                        {isWithinTolerance 
                                            ? t('incomingDeliveries.weightToleranceSuccess') 
                                            : t('incomingDeliveries.weightToleranceError')}
                                    </Alert>
                                );
                            })()}

                            <div className="mt-3">
                                <p><strong>{t('incomingDeliveries.supplier')}:</strong> {selectedDelivery.Name}</p>
                                <p><strong>{t('incomingDeliveries.item')}:</strong>{selectedDelivery.Code1} - {selectedDelivery.Description}</p>
                            </div>
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={saveWeighing}>{t('incomingDeliveries.saveWeighing')}</Button>
                    <Button color="secondary" onClick={() => setWeighingModal(false)}>{t('volumes.cancel')}</Button>
                </ModalFooter>
            </Modal>
        </Card>
    );
};

export default withTranslation(IncomingDeliveries);
